import axios from 'axios';
import { getToken, setToken, removeToken } from '../utils/auth';
import { API_BASE_URL } from '../config/runtime';

// Axios 인스턴스 생성
const api = axios.create({
  baseURL: API_BASE_URL,
});

// 요청 인터셉터 - 토큰 자동 추가
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 응답 인터셉터 - 토큰 만료 처리
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // 토큰이 만료되었거나 유효하지 않음
      removeToken();
      // React Router를 사용하므로 강제 리로딩 대신 상태 변경으로 처리
      // window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API 서비스
export const authAPI = {
  // 로그인
  login: async (email: string, password: string) => {
    console.log('API: 로그인 요청', { email });
    const response = await api.post('/auth/login', { email, password });
    console.log('API: 로그인 응답', response.data);
    
    if (response.data.token) {
      setToken(response.data.token);
    }
    
    return response.data;
  },

  // 회원가입 (닉네임 및 추가 개인정보 지원)
  register: async (
    email: string, 
    password: string, 
    name: string, 
    nickname?: string,
    phone?: string,
    birthDate?: string,
    gender?: string
  ) => {
    console.log('API: 회원가입 요청', { email, name, nickname, phone, birthDate, gender });
    const response = await api.post('/auth/register', { 
      email, 
      password, 
      name, 
      nickname,
      phone,
      birthDate,
      gender
    });
    console.log('API: 회원가입 응답', response.data);
    return response.data;
  },

  // 사용자 정보 조회
  getMe: async () => {
    console.log('API: 사용자 정보 조회 요청');
    const response = await api.get('/auth/me');
    console.log('API: 사용자 정보 조회 응답', response.data);
    return response.data;
  },

  // 프로필 조회 (상세 정보 포함)
  getProfile: async () => {
    console.log('API: 프로필 조회 요청');
    const response = await api.get('/auth/profile');
    console.log('API: 프로필 조회 응답', response.data);
    return response.data;
  },

  // 토큰 검증
  verifyToken: async () => {
    console.log('API: 토큰 검증 요청');
    const response = await api.get('/auth/verify');
    console.log('API: 토큰 검증 응답', response.data);
    return response.data;
  },

  // 프로필 업데이트
  updateProfile: async (profileData: {
    name?: string;
    nickname?: string;
    email?: string;
    phone?: string;
    birth_date?: string;
    gender?: string;
  }) => {
    console.log('API: 프로필 업데이트 요청', profileData);
    const response = await api.put('/auth/profile', profileData);
    console.log('API: 프로필 업데이트 응답', response.data);
    return response.data;
  },

  // 비밀번호 변경
  changePassword: async (currentPassword: string, newPassword: string) => {
    console.log('API: 비밀번호 변경 요청');
    const response = await api.put('/auth/password', { 
      currentPassword, 
      newPassword 
    });
    console.log('API: 비밀번호 변경 응답', response.data);
    return response.data;
  },

  // 닉네임 중복 확인
  checkNickname: async (nickname: string) => {
    console.log('API: 닉네임 중복 확인 요청', { nickname });
    const response = await api.get(`/auth/check/nickname/${encodeURIComponent(nickname)}`);
    console.log('API: 닉네임 중복 확인 응답', response.data);
    return response.data;
  },

  // 이메일 중복 확인
  checkEmail: async (email: string) => {
    console.log('API: 이메일 중복 확인 요청', { email });
    const response = await api.post('/auth/check/email', { email });
    console.log('API: 이메일 중복 확인 응답', response.data);
    return response.data;
  },

  // 사용자 검색
  searchUsers: async (query: string, limit?: number, offset?: number) => {
    console.log('API: 사용자 검색 요청', { query, limit, offset });
    const response = await api.get('/auth/search', {
      params: { q: query, limit, offset }
    });
    console.log('API: 사용자 검색 응답', response.data);
    return response.data;
  },

  // 회원탈퇴 (완전 삭제)
  deleteAccount: async (data: { password?: string; confirmText: string }) => {
    console.log('API: 회원탈퇴 요청', { confirmText: data.confirmText, hasPassword: !!data.password });
    const response = await api.delete('/auth/account', {
      data: data
    });
    console.log('API: 회원탈퇴 응답', response.data);
    return response.data;
  },

  // 로그아웃
  logout: () => {
    console.log('API: 로그아웃');
    removeToken();
  }
};

export default api;
